/**
 * Component: VenueSelectionStep
 * @description Allows users to search and select a venue from a list. Filters venues dynamically based on user input and updates the selected venue in the Redux store.
 *
 * @props
 *   @param {function} onVenueSelect - Callback function triggered when a venue is selected.
 *
 * @redux
 *   @action fetchVenues - Fetches the list of venues from Firestore.
 *   @action setVenueInfo - Updates the Redux state with the selected venue information.
 *   @selector venues - Retrieves the list of venues, their status, and any errors from the Redux store.
 *
 * @state
 *   @state {string} searchTerm - Stores the search input for filtering the venue list.
 *
 * @methods
 *   @function handleVenueSelect - Handles venue selection, triggering the `onVenueSelect` callback and updating Redux with the selected venue.
 *
 * @render
 *   - Displays a search input for filtering venues.
 *   - Shows a list of venues matching the search term.
 *   - Handles loading and error states while fetching venues.
 *
 * @example
 * <VenueSelectionStep onVenueSelect={(venueId) => console.log("Selected venue:", venueId)} />
 */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton, InputAdornment, Typography, Stack } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { VenueStepContainer, StyledList, StyledListItem, StyledSearchField } from './VenueSelectionStep.styles';
import { fetchVenues } from '../../../../../../redux/venues/venuesThunks';
import { setVenueInfo } from '../../../../../../redux/slices/currentUserSlice';

const VenueSelectionStep = ({ onVenueSelect }) => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');

  // Select venues from Redux store
  const { venues, status, error } = useSelector((state) => state.venues);

  // Fetch venues when component mounts
  useEffect(() => {
    dispatch(fetchVenues());
  }, [dispatch]);

  // Filter venues based on the search term
  const filteredVenues = venues.filter((venue) => venue.name.toLowerCase().includes(searchTerm.toLowerCase()));

  // Handle venue selection
  const handleVenueSelect = (venueId) => {
    if (onVenueSelect) {
      onVenueSelect(venueId);
    }
    const selectedVenue = venues.find((venue) => venue.id === venueId);
    if (selectedVenue) {
      dispatch(setVenueInfo(selectedVenue));
    }
  };

  return (
    <VenueStepContainer>
      {/* Search field */}
      <StyledSearchField
        variant="outlined"
        placeholder="Search"
        fullWidth
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {/* Conditional rendering based on the loading status */}
      {status === 'failed' ? (
        <p>Error: {error}</p>
      ) : filteredVenues.length > 0 ? (
        <StyledList>
          {filteredVenues.map((venue, index) => (
            <StyledListItem key={index} onClick={() => handleVenueSelect(venue.id)}>
              <Stack>
                <Typography variant="bodyRegular">{venue.name} </Typography>
                <Typography variant="subheadlineRegular" color="#EBEBF599">
                  {venue.address}
                </Typography>
              </Stack>
              <IconButton>
                <ArrowForwardIosIcon
                  style={{
                    fontSize: '17px',
                    fontWeight: 600,
                    lineHeight: '22px',
                    color: 'rgba(235, 235, 245, 0.7)',
                  }}
                />
              </IconButton>
            </StyledListItem>
          ))}
        </StyledList>
      ) : (
        <p>No venues found.</p>
      )}
    </VenueStepContainer>
  );
};

export default VenueSelectionStep;
